import { App } from '@capacitor/app';
import { useEffect, useRef, type ReactNode } from 'react';

import { useAuth } from '@/store/auth/auth.store';
import { useWebSocket } from '@/store/websocket/websocket.store';

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000];

type WebSocketProviderProps = {
  children: ReactNode;
};

export const WebSocketProvider = ({ children }: WebSocketProviderProps) => {
  const isLoggedIn = useAuth((state) => state.isLoggedIn);
  const connect = useWebSocket((state) => state.connect);
  const disconnect = useWebSocket((state) => state.disconnect);
  const connectionStatus = useWebSocket((state) => state.connectionStatus);

  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      reconnectAttempts.current = 0;
      disconnect();
      return;
    }

    connect();

    // appStateChange is reliable in a Capacitor WebView (and fires on web too), unlike visibilitychange.
    const handleResume = () => {
      const { connection, connectionStatus } = useWebSocket.getState();
      // Live socket → leave it; a dead-but-OPEN socket is caught by the store heartbeat.
      if (connection?.readyState === WebSocket.OPEN && connectionStatus === 'connected') return;

      // disconnect() first: connect() no-ops on a still-OPEN socket, so drop the stale one first.
      reconnectAttempts.current = 0;
      disconnect();
      connect();
    };

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) handleResume();
    });

    return () => {
      void listener.then((l) => l.remove());
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      disconnect();
    };
  }, [isLoggedIn, connect, disconnect]);

  useEffect(() => {
    if (!isLoggedIn) return;

    if (connectionStatus === 'connected') {
      reconnectAttempts.current = 0;
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      return;
    }

    if (connectionStatus !== 'error' && connectionStatus !== 'disconnected') return;

    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      console.error(`[WebSocket] Failed to connect after ${MAX_RECONNECT_ATTEMPTS} attempts.`);
      return;
    }

    if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    const delay = RECONNECT_DELAYS[reconnectAttempts.current] || 16000;
    reconnectTimeout.current = setTimeout(() => {
      reconnectAttempts.current += 1;
      connect();
    }, delay);
  }, [connectionStatus, isLoggedIn, connect]);

  return <>{children}</>;
};
